import React, { useState, useEffect, useRef, useMemo } from "react";

/**
 * dataList: [
 *   {
 *     trip_id: string,
 *     path: [[lng, lat], [lng, lat], ...],
 *     timestamps: [t0, t1, t2, ...]
 *   },
 *   ...
 * ]
 * 
 * currentTime: 現在の時刻（秒）
 * baseHistoryLength: 履歴最大数
 * R, Color, Opacity, Type: 各描画・属性パラメータ
 *
 * 【最終的に返したい形】
 * [
 *   { lat, lng, r, color, opacity, type },
 *   { lat, lng, r, color, opacity, type },
 *   ...
 * ]
 *
 * 全trip_idの履歴をフラットに統合した1つの配列を返す
 */
export function useTrajectory2({
  dataList,
  currentTime,
  baseHistoryLength,
  R = 10,
  Color = "4169e1",
  Opacity = 0.5,
  Type = "none",
  isActive,
}) {


  // 各 trip_id の履歴を Dict形式で管理: { trip_id: [ {lat, lng, ...}, ... ], ... }
  const [historiesDict, setHistoriesDict] = useState({});

  // ベジェ曲線制御点をキャッシュする
  const controlPointsRef = useRef({});


  // 各 trip_id の historyLength をキャッシュする
  const historyLengthRef = useRef({});


  // 1) dataList が変わったら、ベジェ曲線の乱数制御点を生成してキャッシュ
  useEffect(() => {

    if (!dataList) return;

    if (!isActive) {
      // isActive が false の場合は履歴をリセット
      setHistoriesDict({});
      //console.log("non")
      return;     
    }

   
    const newControlPoints = {};
    const newHistoryLengths = {};

    dataList.forEach((item) => {
      //console.log("一つ目のuseEffectが叩かれた",isActive,dataList);

      //console.log("isActive use",isActive)
      if (!item.path || item.path.length < 2 || !item.timestamps) return;
      const { trip_id, path, timestamps } = item;

      

      const segments = [];
      for (let i = 0; i < item.path.length - 1; i++) {
        const [lng0, lat0] = item.path[i];
        const [lng1, lat1] = item.path[i + 1];
        const t0 = timestamps[i];
        const t1 = timestamps[i + 1];
        const timeDiff = t1 - t0;
        if (timeDiff <= 0) continue; // 時間差がゼロ以下の場合はスキップ

        // 距離を計算（緯度経度での直線距離）
        const distance = Math.sqrt((lng1 - lng0) ** 2 + (lat1 - lat0) ** 2);

        // 速度を計算
        const speed = distance / timeDiff;


        // スケールを速度に基づいて調整
        // 速度が高いほど scale を小さく、低いほど大きくする
        const minScale = 0.00001; // スケールの最小値
        const maxScale = 0.01;  // スケールの最大値
        // 速度が高くなるほどスケールを急激に小さくするために、速度の二乗を使用
        const scale = Math.max(minScale, maxScale - Math.pow(speed, 2) * 0.0001);


 
        const cp1 = {
          lng: lng0 + (lng1 - lng0) / 3 + (Math.random() - 0.5) * scale,
          lat: lat0 + (lat1 - lat0) / 3 + (Math.random() - 0.5) * scale,
        };
        const cp2 = {
          lng: lng0 + (lng1 - lng0) * (2 / 3) + (Math.random() - 0.5) * scale,
          lat: lat0 + (lat1 - lat0) * (2 / 3) + (Math.random() - 0.5) * scale,
        };
        segments.push({ cp1, cp2 });
      }

      newControlPoints[item.trip_id] = segments;
      newHistoryLengths[item.trip_id] = 10 + Math.floor(Math.random() * 11); // 10～20の間でランダム
  
    });

    controlPointsRef.current = newControlPoints;
    historyLengthRef.current = newHistoryLengths;
  }, [isActive,dataList]);

  // 2) currentTime が変わるたびに全trip_idの「最新フレームの履歴アイテム」を追加
  useEffect(() => {
    if (!dataList) return;
    if (!isActive) return;

    // historiesDict をコピーして更新
    const newHistoriesDict = { ...historiesDict };

    dataList.forEach((item) => {
      const { trip_id, path, timestamps } = item;
      if (!path || !timestamps || path.length < 2 || timestamps.length < 2) {
        return; 
      }

      const minTime = timestamps[0];
      const maxTime = timestamps[timestamps.length - 1];
      if (currentTime < minTime || currentTime > maxTime) {
        // 範囲外なら履歴クリア
        newHistoriesDict[trip_id] = [];
        return;
      }

      // 区間探索
      let i = 0;
      while (i < timestamps.length - 1 && currentTime > timestamps[i + 1]) {
        i++;
      }

      // ベジェ補間
      const t0 = timestamps[i];
      const t1 = timestamps[i + 1];
      const alpha = (currentTime - t0) / (t1 - t0);

      const [lng0, lat0] = path[i];
      const [lng1, lat1] = path[i + 1];
      const segments = controlPointsRef.current[trip_id] || [];

      const { cp1, cp2 } = segments[i] || {
        cp1: { lng: lng0, lat: lat0 },
        cp2: { lng: lng1, lat: lat1 },
      };

      const lng =
        (1 - alpha) ** 3 * lng0 +
        3 * (1 - alpha) ** 2 * alpha * cp1.lng +
        3 * (1 - alpha) * alpha ** 2 * cp2.lng +
        alpha ** 3 * lng1;

      const lat =
        (1 - alpha) ** 3 * lat0 +
        3 * (1 - alpha) ** 2 * alpha * cp1.lat +
        3 * (1 - alpha) * alpha ** 2 * cp2.lat +
        alpha ** 3 * lat1;


        const frequency = 0.01;
        const amplitude = 20;

        // 毎フレームごとに乱数位相を決定
        const randomOffset = Math.random() * 2 * Math.PI; // 0 ~ 2π

        // sin関数の結果が0にならないようにオフセットを追加
        const sinValue = Math.sin(frequency * currentTime + randomOffset);
        const adjustedSinValue = (sinValue + 1) / 2; // sinの範囲を0から1に調整

        const dynamicRadius =
          R + amplitude * adjustedSinValue;

      // 履歴アイテム
      const newItem = {
        lat,
        lng,
        r: dynamicRadius,
        color: Color,
        opacity: Opacity,
        type: Type,
      };

      // 履歴を更新
      const oldHist = newHistoriesDict[trip_id] || [];
      newHistoriesDict[trip_id] = [...oldHist, newItem].slice(-baseHistoryLength);
    });

    setHistoriesDict(newHistoriesDict);
  }, [dataList, currentTime, baseHistoryLength, R, Color, Opacity, Type]);

  // 3) 全trip_idの履歴を "一つの配列" にフラット化して返す
  //    [{ lat, lng, r, color, opacity, type }, ... ]
  const allHistoriesArray = useMemo(() => {
    // historiesDict は { trip_id: [historyItems...] }
    // これを全部まとめて一つの配列にする
    return Object.values(historiesDict).flat();
  }, [historiesDict ,isActive]);

  return allHistoriesArray;
}

export default useTrajectory2